import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';

interface Order {
  id: number;
  symbol: string;
  type: 'buy' | 'sell';
  orderType: 'market' | 'limit' | 'stop';
  amount: number;
  price: number;
  status: 'pending' | 'filled' | 'cancelled' | 'partial';
  timestamp: string;
  filled: number;
}

const OrdersPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'open' | 'history'>('open');
  const [filterStatus, setFilterStatus] = useState<string>('all');

  const orders: Order[] = [
    {
      id: 1,
      symbol: 'APT/USDT',
      type: 'buy',
      orderType: 'limit',
      amount: 100,
      price: 8.25,
      status: 'pending',
      timestamp: '2024-01-15 14:30:22',
      filled: 0
    },
    {
      id: 2,
      symbol: 'ETH/USDT',
      type: 'sell',
      orderType: 'market',
      amount: 0.5,
      price: 2450.00,
      status: 'filled',
      timestamp: '2024-01-15 13:45:18',
      filled: 0.5
    },
    {
      id: 3,
      symbol: 'BTC/USDT',
      type: 'buy',
      orderType: 'stop',
      amount: 0.01,
      price: 42000.00,
      status: 'partial',
      timestamp: '2024-01-15 12:20:15',
      filled: 0.007
    },
    {
      id: 4,
      symbol: 'SOL/USDT',
      type: 'sell',
      orderType: 'limit',
      amount: 25,
      price: 98.50,
      status: 'cancelled',
      timestamp: '2024-01-15 11:15:30',
      filled: 0
    }
  ];

  const openOrders = orders.filter(order => ['pending', 'partial'].includes(order.status));
  const orderHistory = orders.filter(order => ['filled', 'cancelled'].includes(order.status));

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'text-yellow-400';
      case 'filled': return 'text-green-400';
      case 'cancelled': return 'text-red-400';
      case 'partial': return 'text-blue-400';
      default: return 'text-green-400';
    }
  };

  const getTypeColor = (type: string) => {
    return type === 'buy' ? 'text-green-400' : 'text-red-400';
  };

  const filteredOrders = (activeTab === 'open' ? openOrders : orderHistory).filter(order => 
    filterStatus === 'all' || order.status === filterStatus
  );

  return (
    <div className="h-full bg-[#0a0a1a] text-green-400 font-mono p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-green-400 mb-2">Order Management</h1>
          <div className="text-green-400/70 text-sm">
            Monitor and manage your trading orders
          </div>
        </div>
        <Button className="bg-green-500/20 hover:bg-green-500/30 text-green-400 border border-green-500/30">
          + New Order
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid md:grid-cols-4 gap-4 mb-6">
        <Card className="p-4 bg-[#0d0d2a] border-green-500/30">
          <div className="text-green-400/70 text-sm mb-1">Open Orders</div>
          <div className="text-2xl font-bold text-green-400">{openOrders.length}</div>
        </Card>
        <Card className="p-4 bg-[#0d0d2a] border-green-500/30">
          <div className="text-green-400/70 text-sm mb-1">Filled Today</div>
          <div className="text-2xl font-bold text-green-400">12</div>
        </Card>
        <Card className="p-4 bg-[#0d0d2a] border-green-500/30">
          <div className="text-green-400/70 text-sm mb-1">Total Volume</div>
          <div className="text-2xl font-bold text-green-400">$24.5K</div>
        </Card>
        <Card className="p-4 bg-[#0d0d2a] border-green-500/30">
          <div className="text-green-400/70 text-sm mb-1">Success Rate</div>
          <div className="text-2xl font-bold text-green-400">87.3%</div>
        </Card>
      </div>

      {/* Controls */}
      <div className="flex items-center justify-between mb-6">
        {/* Tab Navigation */}
        <div className="flex space-x-1">
          <Button
            onClick={() => setActiveTab('open')}
            className={`px-4 py-2 text-sm rounded transition-colors ${
              activeTab === 'open'
                ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                : 'bg-transparent text-green-400/70 hover:bg-green-500/10 hover:text-green-400'
            }`}
          >
            Open Orders ({openOrders.length})
          </Button>
          <Button
            onClick={() => setActiveTab('history')}
            className={`px-4 py-2 text-sm rounded transition-colors ${
              activeTab === 'history'
                ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                : 'bg-transparent text-green-400/70 hover:bg-green-500/10 hover:text-green-400'
            }`}
          >
            Order History
          </Button>
        </div>

        {/* Filters */}
        <div className="flex items-center space-x-4">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="bg-[#0d0d2a] border border-green-500/30 text-green-400 text-sm rounded px-3 py-1 focus:border-green-400 focus:outline-none"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="filled">Filled</option>
            <option value="cancelled">Cancelled</option>
            <option value="partial">Partial</option>
          </select>
          <Input
            placeholder="Search orders..."
            className="w-48 bg-transparent border-green-500/30 text-green-400 placeholder-green-400/50 focus:border-green-400"
          />
        </div>
      </div>

      {/* Orders Table */}
      <Card className="bg-[#0d0d2a] border-green-500/30">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-green-500/30">
                <th className="text-left p-4 text-green-400/70 text-sm font-medium">Order ID</th>
                <th className="text-left p-4 text-green-400/70 text-sm font-medium">Symbol</th>
                <th className="text-left p-4 text-green-400/70 text-sm font-medium">Type</th>
                <th className="text-left p-4 text-green-400/70 text-sm font-medium">Order Type</th>
                <th className="text-right p-4 text-green-400/70 text-sm font-medium">Amount</th>
                <th className="text-right p-4 text-green-400/70 text-sm font-medium">Price</th>
                <th className="text-right p-4 text-green-400/70 text-sm font-medium">Filled</th>
                <th className="text-center p-4 text-green-400/70 text-sm font-medium">Status</th>
                <th className="text-left p-4 text-green-400/70 text-sm font-medium">Time</th>
                <th className="text-center p-4 text-green-400/70 text-sm font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.map((order) => (
                <tr key={order.id} className="border-b border-green-500/20 hover:bg-green-500/5">
                  <td className="p-4 text-green-400 text-sm">#{order.id.toString().padStart(6, '0')}</td>
                  <td className="p-4 text-green-300 text-sm font-medium">{order.symbol}</td>
                  <td className="p-4">
                    <span className={`text-sm font-medium ${getTypeColor(order.type)}`}>
                      {order.type.toUpperCase()}
                    </span>
                  </td>
                  <td className="p-4 text-green-400/70 text-sm capitalize">{order.orderType}</td>
                  <td className="p-4 text-right text-green-400 text-sm">{order.amount}</td>
                  <td className="p-4 text-right text-green-400 text-sm">${order.price.toFixed(2)}</td>
                  <td className="p-4 text-right text-green-400 text-sm">
                    {order.filled > 0 ? `${order.filled} (${((order.filled / order.amount) * 100).toFixed(1)}%)` : '-'}
                  </td>
                  <td className="p-4 text-center">
                    <span className={`text-xs px-2 py-1 rounded ${getStatusColor(order.status)} bg-current bg-opacity-20`}>
                      <span style={{color: 'black'}}>{order.status.toUpperCase()}</span>
                    </span>
                  </td>
                  <td className="p-4 text-green-400/70 text-sm">{order.timestamp}</td>
                  <td className="p-4 text-center">
                    <div className="flex justify-center space-x-1">
                      {order.status === 'pending' && (
                        <>
                          <Button size="sm" variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10 text-xs">
                            Cancel
                          </Button>
                          <Button size="sm" variant="outline" className="border-green-500/30 text-green-400 hover:bg-green-500/10 text-xs">
                            Modify
                          </Button>
                        </>
                      )}
                      {order.status === 'partial' && (
                        <Button size="sm" variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10 text-xs">
                          Cancel
                        </Button>
                      )}
                      {['filled', 'cancelled'].includes(order.status) && (
                        <Button size="sm" variant="outline" className="border-green-500/30 text-green-400/70 hover:bg-green-500/10 text-xs">
                          Details
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredOrders.length === 0 && (
          <div className="text-center py-12">
            <div className="text-green-400/50 text-lg mb-2">No orders found</div>
            <div className="text-green-400/30 text-sm">
              {activeTab === 'open' ? 'No open orders at the moment' : 'No order history available'}
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

export default OrdersPage;
